import {
  Children,
  CSSProperties,
  Fragment,
  forwardRef,
  isValidElement,
  cloneElement,
  createElement,
  useState,
  useEffect,
  useCallback,
  memo
} from 'react';
import {
  DatagridCell,
  DatagridRowProps,
  ExpandRowButton,
  linkToRecord
} from 'react-admin';
import { useHistory } from 'react-router-dom';
import classnames from 'classnames';
import isEqual from 'lodash/isEqual';
import { TableCell, TableRow, Checkbox } from '@material-ui/core';

const computeNbColumns = (
  expand: SortableDatagridRowProps['expand'],
  children: SortableDatagridRowProps['children'],
  hasBulkActions: SortableDatagridRowProps['hasBulkActions']
) =>
  expand
    ? 1 + // show expand button
      (hasBulkActions ? 1 : 0) + // checkbox column
      Children.toArray(children).filter(child => !!child).length // non-null children
    : 0; // we don't need to compute columns if there is no expand panel;

const draggingStyles = (isDragging?: boolean, style?: CSSProperties) => {
  if (!isDragging) {
    return style;
  }

  return {
    ...style,
    cursor: 'grabbing !important',
    opacity: 0,
    border: '2px dashed rgba(0,0,0,0.2)'
  };
};

type SortableDatagridRowProps = DatagridRowProps & {
  isDragging?: boolean;
};

const DatagridRow = forwardRef<HTMLTableRowElement, SortableDatagridRowProps>(
  (props, ref) => {
    const {
      basePath,
      children,
      classes,
      className,
      expand,
      hasBulkActions,
      hover,
      id,
      onToggleItem,
      record,
      resource,
      rowClick,
      selected,
      style,
      selectable,
      isDragging,
      ...rest
    } = props;

    const [expanded, setExpanded] = useState(false);
    const [nbColumns, setNbColumns] = useState(
      computeNbColumns(expand, children, hasBulkActions)
    );
    useEffect(() => {
      // Fields can be hidden dynamically based on permissions;
      // The expand panel must span over the remaining columns
      // So we must recompute the number of columns to span on
      const newNbColumns = computeNbColumns(expand, children, hasBulkActions);
      if (newNbColumns !== nbColumns) {
        setNbColumns(newNbColumns);
      }
    }, [expand, nbColumns, children, hasBulkActions]);

    const history = useHistory();

    const handleToggleExpand = useCallback(
      event => {
        setExpanded(!expanded);
        event.stopPropagation();
      },
      [expanded]
    );
    const handleToggleSelection = useCallback(
      event => {
        if (id) {
          onToggleItem?.(id, event);
        }
        event.stopPropagation();
      },
      [id, onToggleItem]
    );
    const handleClick = useCallback(
      async event => {
        if (!rowClick || !id || !basePath || !record) {
          return;
        }

        event.persist();

        const effect =
          typeof rowClick === 'function'
            ? await rowClick(id, basePath, record)
            : rowClick;
        switch (effect) {
          case 'edit':
            history.push(linkToRecord(basePath, id));
            return;
          case 'show':
            history.push(linkToRecord(basePath, id, 'show'));
            return;
          case 'expand':
            handleToggleExpand(event);
            return;
          case 'toggleSelection':
            handleToggleSelection(event);
            return;
          default:
            if (effect) {
              history.push(effect);
            }
            return;
        }
      },
      [
        basePath,
        history,
        handleToggleExpand,
        handleToggleSelection,
        id,
        record,
        rowClick
      ]
    );

    return (
      <Fragment>
        <TableRow
          ref={ref}
          className={className}
          key={id}
          style={draggingStyles(isDragging, style)}
          hover={hover}
          onClick={handleClick}
          {...rest}
        >
          {expand && (
            <TableCell padding="none" className={classes?.expandIconCell}>
              <ExpandRowButton
                classes={classes}
                expanded={expanded}
                onClick={handleToggleExpand}
                expandContentId={`${id}-expand`}
              />
            </TableCell>
          )}
          {hasBulkActions && (
            <TableCell padding="checkbox">
              {selectable && (
                <Checkbox
                  color="primary"
                  className={`select-item ${classes?.checkbox}`}
                  checked={selected}
                  onClick={handleToggleSelection}
                />
              )}
            </TableCell>
          )}
          {Children.map(children, (field, index) =>
            isValidElement(field) ? (
              <DatagridCell
                key={`${id}-${field.props.source || index}`}
                className={classnames(
                  `column-${field.props.source}`,
                  classes?.rowCell
                )}
                record={record}
                {...{ field, basePath, resource }}
              />
            ) : null
          )}
        </TableRow>
        {expand && expanded && (
          <TableRow key={`${id}-expand`} id={`${id}-expand`}>
            <TableCell colSpan={nbColumns}>
              {isValidElement(expand)
                ? cloneElement(expand, {
                    record,
                    basePath,
                    resource,
                    id: String(id)
                  })
                : record &&
                  basePath &&
                  resource &&
                  createElement(expand, {
                    record,
                    basePath,
                    resource,
                    id: String(id)
                  })}
            </TableCell>
          </TableRow>
        )}
      </Fragment>
    );
  }
);

DatagridRow.defaultProps = {
  hasBulkActions: false,
  hover: true,
  isDragging: false,
  selected: false,
  selectable: true
};

const areEqual = (
  prevProps: SortableDatagridRowProps,
  nextProps: SortableDatagridRowProps
) => {
  const { children: _, ...prevPropsWithoutChildren } = prevProps;
  const { children: __, ...nextPropsWithoutChildren } = nextProps;
  return isEqual(prevPropsWithoutChildren, nextPropsWithoutChildren);
};

export const PureDatagridRow = memo(DatagridRow, areEqual);

PureDatagridRow.displayName = 'PureDatagridRow';

export default DatagridRow;
